import type { MouseEvent } from 'react';
import { Box } from '@interstate/components/Box';
import { Typography } from '@interstate/components/Typography';
import { dateUtils, formatNumberAndCents } from '@makemydeal/dr-common-utils';
import type { IDealChangeRecord } from '@makemydeal/dr-platform-types';
import { AuditLogDateFields, AuditLogFieldOverrideType, AuditLogFieldPaths } from '@makemydeal/dr-platform-types';
import type { InfoCardField } from '../../types/AuditLog';
import Badge from '../Badge/Badge.interstate';
import { TimeStyled } from '../Tables/Table.style';

interface TransformArrayValueOptions {
    onArrayClick?: (value: any[], e: MouseEvent) => void;
}

export const transformArrayValue = (value: any, options?: TransformArrayValueOptions, fieldPath?: string) => {
    const handleClick = (e: MouseEvent) => {
        // Stop propagation to prevent table row expansion
        e.stopPropagation();

        // Call custom handler if provided
        if (options?.onArrayClick && Array.isArray(value)) {
            options.onArrayClick(value, e);
        }
    };

    return typeof value === 'object' && Array.isArray(value) ? (
        <Box className="array-value-clickable" data-testid="array-value-clickable" onClick={handleClick} sx={{ cursor: 'pointer' }}>
            <Typography color="#318AD3" variant="body-md">{`${value.length}${value.length <= 1 ? ' item' : ' items'}`}</Typography>
        </Box>
    ) : value && AuditLogDateFields.includes(String(fieldPath)) ? (
        `${new Date(value).toLocaleDateString('en-US', {
            year: '2-digit',
            month: '2-digit',
            day: '2-digit'
        })} ${dateUtils.formatAsTime(new Date(value))}` // Format date and time
    ) : (
        value
    );
};

// check if array has isManual Fee/Tax items
const hasManualItems = (items: any[], overrideType: AuditLogFieldOverrideType): boolean => {
    if (!Array.isArray(items)) return false;
    return items.some((item) => item[overrideType] === true);
};

export const transformationFieldName = (record: Record<string, any>) => {
    const hasManualFees =
        record.fieldPath === AuditLogFieldPaths.FEES && hasManualItems(record.currentValue, AuditLogFieldOverrideType.Fee);
    const hasManualTaxes =
        record.fieldPath === AuditLogFieldPaths.TAXES && hasManualItems(record.currentValue, AuditLogFieldOverrideType.Tax);
    return (
        <Box display="flex" flexDirection="column" gap="1px">
            <Typography variant="body-sm">{record.displayName}</Typography>
            {(hasManualFees || hasManualTaxes) && <Badge variant="dark">Overrides</Badge>}
        </Box>
    );
};

export const transformationDateTime = (date: string) => {
    return (
        <Box display="flex" flexDirection="column">
            <Typography variant="body-sm">
                {date ? new Date(date).toLocaleDateString('en-US', { year: '2-digit', month: '2-digit', day: '2-digit' }) : '--'}
            </Typography>
            <TimeStyled variant="body-sm">{dateUtils.formatAsTime(new Date(date))}</TimeStyled>
        </Box>
    );
};
export const transformationModifiedBy = (value: string) => {
    return (
        <Box display="flex" flexDirection="column">
            <Typography variant="body-sm">{value}</Typography>
        </Box>
    );
};

export const transformModalArrayField = (fieldDetails: IDealChangeRecord, normalizedData: Record<string, any>) => {
    const cloneRecord = { ...normalizedData };
    let titleText = '';
    if (fieldDetails.titleProp) {
        titleText = cloneRecord[fieldDetails.titleProp];
        delete cloneRecord[fieldDetails.titleProp];
    }
    // Helper to render a single field
    const renderField = (key: string, value: any) => {
        const isInvalid = value === undefined || value === null || value === '';
        return (
            <Box display="flex" width="100%" flexDirection="row" justifyContent="space-between" lineHeight="1.5" key={key}>
                <Typography key={`key-${key}`} variant="body-md" sx={{ flex: 1, width: '50%', textTransform: 'capitalize' }}>
                    {`${key}:`}
                </Typography>
                <Typography key={`value-${key}`} variant="body-md" sx={{ flex: 1, paddingLeft: '1rem', width: '50%' }}>
                    {isInvalid ? '--' : String(value)}
                </Typography>
            </Box>
        );
    };

    // Main rendering logic
    return (
        <Box display="flex" flexDirection="column">
            {titleText && (
                <Box display="flex" width="100%" flexDirection="row" justifyContent="space-between" lineHeight="1.5">
                    <Typography variant="body-md" sx={{ flex: 1, textTransform: 'capitalize' }}>
                        {titleText}
                    </Typography>
                    <Typography variant="body-md" sx={{ flex: 1, paddingLeft: '1rem' }}></Typography>
                </Box>
            )}
            {Object.entries(cloneRecord)
                .filter(([_, item]) => !Array.isArray(item))
                .map(([key, value]) => {
                    if (value && typeof value === 'object') {
                        return Object.entries(value).map(([nestedKey, nestedValue]) => renderField(nestedKey, nestedValue));
                    } else {
                        // Otherwise, render as usual
                        return renderField(key, value);
                    }
                })}
        </Box>
    );
};

export const normalizeChangeLogData = (data: {
    previousValue?: Array<Record<string, any>>;
    currentValue?: Array<Record<string, any>>;
}) => {
    const prev = data.previousValue || [];
    const curr = data.currentValue || [];
    const maxLength = Math.max(prev.length, curr.length);

    return Array.from({ length: maxLength }, (_, index) => ({
        previousValue: index < prev.length ? prev[index] : {},
        currentValue: index < curr.length ? curr[index] : {}
    }));
};

export const renderFieldValue = (field: InfoCardField, value: any) => {
    if (typeof field.render === 'function') {
        return field.render(value);
    }

    let displayValue = value;
    if (field.unit) {
        // Handle both prefix and suffix units
        if (field.unit.startsWith('$')) {
            displayValue = `${field.unit}${formatNumberAndCents(value, 2)}`;
        } else {
            displayValue = `${value} ${field.unit}`;
        }
    }
    return displayValue;
};
